import { Box, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type {
  ObjectSelectConfiguration,
  WorkflowActivityChangeSet,
  WorkflowActivityEditConfiguration,
} from "../../../../../../api/types/workflowTypes";
import type { ChangeSetEditState } from "./changeSetEditState";
import { groupFieldsBySections } from "./changeSetEditState";
import ChangeSetInlineEditTable from "./ChangeSetInlineEditTable";
import { loadObjectSelectOptions } from "./objectSelectOptionsHelper";

interface Props {
  changeSet: WorkflowActivityChangeSet;
  editState: ChangeSetEditState;
  editConfiguration?: WorkflowActivityEditConfiguration;
  onFieldChange: (fieldName: string, value: unknown) => void;
  onObjectChange: (configuration: ObjectSelectConfiguration, selectedObject: ObjectFieldValues | null) => void;
}

const ChangeSetInlineEditSection = ({
  changeSet,
  editState,
  editConfiguration,
  onFieldChange,
  onObjectChange,
}: Props) => {
  const objectSelectConfigurations = useMemo(
    () => editConfiguration?.objectSelectConfigurations ?? [],
    [editConfiguration?.objectSelectConfigurations]
  );

  const loadOptions = useCallback(
    () => loadObjectSelectOptions(objectSelectConfigurations, adminApi.searchObjects),
    [objectSelectConfigurations]
  );

  const [objectSelectOptionsResp, fetchError, { isFetching }] = useFetch(loadOptions, {
    disabled: objectSelectConfigurations.length === 0,
  });

  if (fetchError) {
    logError(fetchError, "[ChangeSetInlineEditSection] loadObjectSelectOptions");
    return <DataLoadingFailed title="Failed to fetch required entities" />;
  }

  if (isFetching) {
    return <InlineLoader />;
  }

  const objectSelectOptions = objectSelectOptionsResp ?? {};
  const fieldGroups = groupFieldsBySections(editState.fields, changeSet.sections);
  const originalValues = new Map(changeSet.fields.map((field) => [field.name, field.newValue]));
  const hasUngroupedFields = fieldGroups.some((group) => group.name === undefined);

  return (
    <Box sx={{ pt: 1 }}>
      {hasUngroupedFields && (
        <Typography variant="subtitle2" sx={{ pb: 1 }}>
          {changeSet.name}
        </Typography>
      )}
      <Stack spacing={4}>
        {fieldGroups.map((group, groupIndex) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Box key={`${group.name ?? "ungrouped"}-${groupIndex}`} sx={{ pt: group.name !== undefined ? 1 : 0 }}>
            {group.name !== undefined && (
              <Typography variant="subtitle2" sx={{ pb: 1 }}>
                {group.name}
              </Typography>
            )}
            <ChangeSetInlineEditTable
              fields={group.fields}
              originalValues={originalValues}
              editConfiguration={editConfiguration}
              objectSelectOptions={objectSelectOptions}
              onFieldChange={onFieldChange}
              onObjectChange={onObjectChange}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ChangeSetInlineEditSection;
